/**
 * * 扩展轴刻度标签的文本矩形可以设置百分比宽度，计算规则为将轴的整体长度均分，每一个区间长度即为百分比的计算基准
 *
 * - 当前实现仅处理了 xAxis
 * - 满足 `xAxis.type: 'category'`
 * - 满足 `xAxis.boundaryGap: true`
 * - 会影响标签的重叠检测
 *
 * @example
 * ```javascript
 * {
 *   type: 'category',
 *   axisLabel: {
 *     width: '100%',
 *   },
 *   data: [
 *     {
 *       textStyle: {
 *         width: '100%',
 *       }
 *     }
 *   ]
 * }
 * ```
 */
import { type EChartsExtensionInstallRegisters } from 'echarts/lib/extension';
import { type default as ExtensionAPI } from 'echarts/lib/core/ExtensionAPI';
import { type default as CartesianAxisModel } from 'echarts/lib/coord/cartesian/AxisModel';
import { type default as CartesianAxisView } from 'echarts/lib/component/axis/CartesianAxisView';
import { type default as OrdinalScale } from 'echarts/lib/scale/Ordinal';
import Model from 'echarts/lib/model/Model';
import { parsePercent } from 'zrender/lib/contain/text';
import { isObject } from 'zrender/lib/core/util';
import { getAxisLabelEls } from './helper';

/**
 * 参考实现 @see https://github.com/apache/echarts/blob/5.3.3/src/component/axis/AxisBuilder.ts#L728
 */
function convertTheWidthToASpecificValue(compModal: CartesianAxisModel, api: ExtensionAPI) {
  const { axis } = compModal;
  const show = compModal.get(['axisLabel', 'show']);

  /** @see https://github.com/apache/echarts/blob/5.3.3/src/component/axis/AxisBuilder.ts#L737 */
  if (!compModal.get('show') || !show || axis.scale.isBlank() || !compModal.get('boundaryGap')) {
    return;
  }

  const bandWidth = axis.getBandWidth();
  const compView = api.getViewOfComponentModel(compModal) as CartesianAxisView;
  const labels = axis.getViewLabels();
  const labelEls = getAxisLabelEls(compView);
  const labelModel = compModal.getModel('axisLabel');
  const rawCategoryData = compModal.getCategories && compModal.getCategories(true);

  labels.forEach((labelItem, index) => {
    const tickValue =
      axis.scale.type === 'ordinal'
        ? (axis.scale as OrdinalScale).getRawOrdinalNumber(labelItem.tickValue)
        : labelItem.tickValue;

    let itemLabelModel = labelModel;
    if (rawCategoryData && rawCategoryData[tickValue]) {
      const rawCategoryItem = rawCategoryData[tickValue];
      if (isObject(rawCategoryItem) && rawCategoryItem.textStyle) {
        itemLabelModel = new Model(rawCategoryItem.textStyle, labelModel, compModal.ecModel);
      }
    }

    const width = itemLabelModel.get('width');
    if (!String(width).endsWith('%')) {
      return;
    }

    const rectWidth = parsePercent(width, bandWidth);

    labelEls[index].attr({
      style: {
        width: rectWidth
      }
    });
  });
}

export function install(registers: EChartsExtensionInstallRegisters) {
  // FIXME 最好是能找一个组件渲染之前，但已经布局完成的时机来进行处理
  registers.registerUpdateLifecycle('series:beforeupdate', (ecModal, api) => {
    ecModal.eachComponent((componentType: string, compModal: CartesianAxisModel) => {
      if (componentType === 'xAxis') {
        convertTheWidthToASpecificValue(compModal, api);
      }
    });
  });
}
